import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeOrg, setActiveOrg] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      const stored = localStorage.getItem("bp_org");
      const orgs = data.organisations || [];
      let found = orgs.find((o) => o.id === stored) || orgs[0] || null;
      // Superadmin impersonation: if we stored an org id that isn't in the
      // user's own memberships, hydrate it via the admin endpoint so the
      // whole app treats it like a first-class active org (Programs editor,
      // Billing, Team pages all just work). Non-superadmins ignore this.
      if (data.is_superadmin && stored && (!found || found.id !== stored)) {
        try {
          const r = await api.get(`/admin/orgs/${stored}`);
          if (r.data && r.data.id) found = { ...r.data, _superadmin_view: true };
        } catch (_) { /* org deleted or 403 → fall back to member org */ }
      }
      setActiveOrg(found);
      if (found) localStorage.setItem("bp_org", found.id);
      return data;
    } catch {
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("bp_token")) refresh();
    else { setUser(false); setLoading(false); }
  }, [refresh]);

  const finishLogin = async (data) => {
    localStorage.setItem("bp_token", data.token);
    const me = await refresh();
    return me || data;
  };

  // Legacy direct login/register — kept for backwards compat only; the UI uses
  // the two-step /init + /verify flow below.
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return finishLogin(data);
  };
  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    return finishLogin(data);
  };

  const loginInit = async (email, password) => {
    const { data } = await api.post("/auth/login/init", { email, password });
    return data; // { challenge_id, email, expires_at }
  };
  const loginVerify = async (challenge_id, code) => {
    const { data } = await api.post("/auth/login/verify", { challenge_id, code });
    return finishLogin(data);
  };
  const signupInit = async (name, email, password) => {
    const { data } = await api.post("/auth/signup/init", { name, email, password });
    return data;
  };
  const signupVerify = async (challenge_id, code) => {
    const { data } = await api.post("/auth/signup/verify", { challenge_id, code });
    return finishLogin(data);
  };
  const resendCode = async (challenge_id) => (await api.post("/auth/verification/resend", { challenge_id })).data;

  const logout = async () => {
    localStorage.removeItem("bp_token");
    localStorage.removeItem("bp_org");
    setUser(false); setActiveOrg(null);
  };
  const switchOrg = (org) => { setActiveOrg(org); localStorage.setItem("bp_org", org.id); };

  // Superadmin utilities — impersonateOrg swaps activeOrg into any merchant
  // (fetched via /admin/orgs/:id so it works even without membership).
  // exitImpersonation drops back to the user's own first org.
  const impersonateOrg = async (orgId) => {
    if (!user?.is_superadmin) throw new Error("Superadmin only");
    const { data } = await api.get(`/admin/orgs/${orgId}`);
    const org = { ...data, _superadmin_view: true };
    setActiveOrg(org);
    localStorage.setItem("bp_org", org.id);
    return org;
  };
  const exitImpersonation = () => {
    const own = (user?.organisations || [])[0] || null;
    if (own) { setActiveOrg(own); localStorage.setItem("bp_org", own.id); }
    else { setActiveOrg(null); localStorage.removeItem("bp_org"); }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, activeOrg, setActiveOrg, switchOrg,
                               impersonateOrg, exitImpersonation,
                               login, register, loginInit, loginVerify,
                               signupInit, signupVerify, resendCode, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}
